// client/src/cognitive-flows/components/canvas/icons.js
//
// The Ledger identity's icon set: inline SVG line icons, 24-unit viewBox,
// 1.7 stroke, round caps, currentColor — so type hues and hover colors come
// free from CSS. Replaces every emoji glyph on the canvas surface.

import React from 'react';

function Svg({ size = 16, children, filled = false, ...rest }) {
    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 24 24"
            fill={filled ? 'currentColor' : 'none'}
            stroke={filled ? 'none' : 'currentColor'}
            strokeWidth={1.7}
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
            {...rest}
        >
            {children}
        </svg>
    );
}

export function IconAgent(props) {
    return (
        <Svg {...props}>
            <path d="M12 3.5l1.9 5.3 5.3 1.9-5.3 1.9L12 17.9l-1.9-5.3-5.3-1.9 5.3-1.9z" />
            <path d="M18.5 16.5l.8 2.2 2.2.8-2.2.8-.8 2.2-.8-2.2-2.2-.8 2.2-.8z" strokeWidth={1.4} />
        </Svg>
    );
}

export function IconChart(props) {
    return (
        <Svg {...props}>
            <path d="M4 19V5" />
            <path d="M4 19h16" />
            <path d="M7.5 14.5l3.5-4 3 2.5 4.5-6" />
        </Svg>
    );
}

export function IconTable(props) {
    return (
        <Svg {...props}>
            <rect x="4" y="5" width="16" height="14" rx="1.5" />
            <path d="M4 10h16" />
            <path d="M10 10v9" />
        </Svg>
    );
}

/**
 * Pivot: a table with BOTH a header row and a header column emphasised, and
 * the body divided into four cells — the cross-tabulation a pivot is, and the
 * one thing that distinguishes it at a glance from IconTable (which has a
 * header row only).
 */
export function IconPivot(props) {
    return (
        <Svg {...props}>
            <rect x="4" y="5" width="16" height="14" rx="1.5" />
            <path d="M4 10h16" />
            <path d="M9 5v14" />
            <path d="M14.5 14.5h5" />
        </Svg>
    );
}

export function IconScorecard(props) {
    return (
        <Svg {...props}>
            <circle cx="12" cy="12" r="7.5" />
            <circle cx="12" cy="12" r="2.6" />
        </Svg>
    );
}

export function IconText(props) {
    return (
        <Svg {...props}>
            <path d="M5 7h14" />
            <path d="M5 12h10" />
            <path d="M5 17h7" />
        </Svg>
    );
}

export function IconImage(props) {
    return (
        <Svg {...props}>
            <rect x="3.5" y="4.5" width="17" height="15" rx="1.8" />
            <circle cx="8.5" cy="9.5" r="1.6" />
            <path d="M4 16.5l5-5 3.5 3.5 3-3 4.5 4.5" />
        </Svg>
    );
}

export function IconRefresh(props) {
    return (
        <Svg {...props}>
            <path d="M20 11a8 8 0 1 1-2.3-5.7" />
            <path d="M20 4v5h-5" />
        </Svg>
    );
}

export function IconGear(props) {
    return (
        <Svg {...props}>
            <path d="M4 7h9" />
            <circle cx="16.5" cy="7" r="2.3" />
            <path d="M20 12h-9" />
            <circle cx="7.5" cy="12" r="2.3" />
            <path d="M4 17h9" />
            <circle cx="16.5" cy="17" r="2.3" />
        </Svg>
    );
}

export function IconTrash(props) {
    return (
        <Svg {...props}>
            <path d="M5 7h14" />
            <path d="M9.5 7V5h5v2" />
            <path d="M7 7l1 12.5h8L17 7" />
        </Svg>
    );
}

export function IconGrip(props) {
    return (
        <Svg {...props} filled>
            <circle cx="9.5" cy="6" r="1.4" />
            <circle cx="14.5" cy="6" r="1.4" />
            <circle cx="9.5" cy="12" r="1.4" />
            <circle cx="14.5" cy="12" r="1.4" />
            <circle cx="9.5" cy="18" r="1.4" />
            <circle cx="14.5" cy="18" r="1.4" />
        </Svg>
    );
}

export function IconChevronLeft(props) {
    return (
        <Svg {...props}>
            <path d="M14.5 5.5L8 12l6.5 6.5" />
        </Svg>
    );
}

export function IconPlus(props) {
    return (
        <Svg {...props}>
            <path d="M12 5v14" />
            <path d="M5 12h14" />
        </Svg>
    );
}

export function IconSearch(props) {
    return (
        <Svg {...props}>
            <circle cx="11" cy="11" r="6.2" />
            <path d="M20 20l-4.3-4.3" />
        </Svg>
    );
}

export function IconKebab(props) {
    return (
        <Svg {...props} filled>
            <circle cx="12" cy="5.5" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="12" cy="18.5" r="1.6" />
        </Svg>
    );
}

export function IconCheck(props) {
    return (
        <Svg {...props}>
            <path d="M5 12.5l4.5 4.5L19 7.5" />
        </Svg>
    );
}

export function IconClose(props) {
    return (
        <Svg {...props}>
            <path d="M6.5 6.5l11 11" />
            <path d="M17.5 6.5l-11 11" />
        </Svg>
    );
}

export function IconPencil(props) {
    return (
        <Svg {...props}>
            <path d="M14.5 5.5l4 4L8 20l-4.6.6L4 16z" />
        </Svg>
    );
}

export function IconChat(props) {
    return (
        <Svg {...props}>
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </Svg>
    );
}

export function IconShare(props) {
    return (
        <Svg {...props}>
            <circle cx="18" cy="5.5" r="2.4" />
            <circle cx="6" cy="12" r="2.4" />
            <circle cx="18" cy="18.5" r="2.4" />
            <path d="M8.2 10.9l7.6-4.2" />
            <path d="M8.2 13.1l7.6 4.2" />
        </Svg>
    );
}

export function IconSend(props) {
    return (
        <Svg {...props}>
            <path d="M20.5 3.5L10.5 13.5" />
            <path d="M20.5 3.5l-6.4 17-3.6-7-7-3.6z" />
        </Svg>
    );
}

export function IconDownload(props) {
    return (
        <Svg {...props}>
            <path d="M12 4v10.5" />
            <path d="M7.5 10.5L12 15l4.5-4.5" />
            <path d="M5 19h14" />
        </Svg>
    );
}

export function IconList(props) {
    return (
        <Svg {...props}>
            <path d="M9 6.5h11" />
            <path d="M9 12h11" />
            <path d="M9 17.5h11" />
            <path d="M4.5 6.5h.01" />
            <path d="M4.5 12h.01" />
            <path d="M4.5 17.5h.01" />
        </Svg>
    );
}

export function IconGrid(props) {
    return (
        <Svg {...props}>
            <rect x="4" y="4" width="7" height="7" rx="1.5" />
            <rect x="13" y="4" width="7" height="7" rx="1.5" />
            <rect x="4" y="13" width="7" height="7" rx="1.5" />
            <rect x="13" y="13" width="7" height="7" rx="1.5" />
        </Svg>
    );
}

export function IconChevronDown(props) {
    return (
        <Svg {...props}>
            <path d="M5.5 9.5L12 16l6.5-6.5" />
        </Svg>
    );
}

export function IconPalette(props) {
    return (
        <Svg {...props}>
            <path d="M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 1.4-3.4 2 2 0 0 1 1.4-3.4H18a3 3 0 0 0 3-3A8.6 8.6 0 0 0 12 3z" />
            <circle cx="7.5" cy="11" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="10.5" cy="7.5" r="1.1" fill="currentColor" stroke="none" />
            <circle cx="15" cy="7.5" r="1.1" fill="currentColor" stroke="none" />
        </Svg>
    );
}
